/** Header and footer of the public website (landing, directory, legal pages). */
import { SITE } from "@clinic/shared";
import { Link } from "react-router";
import { LanguageSwitcher, useT } from "./i18n";
import { useMe } from "./session";
import { Logo } from "./ui";

export function SiteHeader({ tone = "dark" }: { tone?: "dark" | "light" }) {
  const me = useMe();
  const { t } = useT();
  const light = tone === "light";
  const quiet = light ? "text-white/85 hover:bg-white/10 hover:text-white" : "text-slate-700 hover:bg-slate-100";
  return (
    <header className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-6 py-5">
      <Link to="/" aria-label={SITE.name}>
        <Logo tone={tone} />
      </Link>
      <nav className="flex flex-wrap items-center gap-1 sm:gap-2" aria-label={t("nav.main")}>
        <Link to="/clinics" className={`hidden rounded-lg px-3 py-2 text-sm font-semibold sm:inline-block ${quiet}`}>
          {t("nav.directory")}
        </Link>
        <LanguageSwitcher tone={tone} className="mx-1" />
        {me.data ? (
          <Link
            to="/app"
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-700"
          >
            {t("nav.open_dashboard")}
          </Link>
        ) : (
          <>
            <Link to="/login" className={`rounded-lg px-3 py-2 text-sm font-semibold ${quiet}`}>
              {t("nav.sign_in")}
            </Link>
            <Link
              to="/signup"
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-700"
            >
              {t("nav.start_free")}
            </Link>
          </>
        )}
      </nav>
    </header>
  );
}

export function SiteFooter() {
  const { t } = useT();
  const { legalName, cvr, address, email } = SITE.company;
  const link = "text-sm text-aqua-100/80 hover:text-white";
  return (
    <footer className="bg-navy-950 text-white">
      <div className="mx-auto grid max-w-6xl gap-10 px-6 py-12 sm:grid-cols-3">
        <div className="space-y-3">
          <Logo tone="light" />
          <p className="text-sm text-aqua-100/80">{t("landing.tagline")}</p>
          <LanguageSwitcher tone="light" />
        </div>
        <nav aria-label={t("footer.product")}>
          <h2 className="text-sm font-semibold text-aqua-300">{t("footer.product")}</h2>
          <ul className="mt-3 space-y-2">
            <li>
              <Link to="/clinics" className={link}>
                {t("nav.directory")}
              </Link>
            </li>
            <li>
              <Link to="/signup" className={link}>
                {t("nav.start_free")}
              </Link>
            </li>
            <li>
              <Link to="/login" className={link}>
                {t("nav.sign_in")}
              </Link>
            </li>
          </ul>
        </nav>
        <nav aria-label={t("footer.legal")}>
          <h2 className="text-sm font-semibold text-aqua-300">{t("footer.legal")}</h2>
          <ul className="mt-3 space-y-2">
            {(["privacy", "terms", "dpa", "cookies"] as const).map((id) => (
              <li key={id}>
                <Link to={`/${id}`} className={link}>
                  {t(`footer.${id}`)}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </div>
      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-2 px-6 py-5 text-xs text-aqua-100/70">
          <p>
            © {new Date().getFullYear()} {legalName || SITE.name}
            {cvr ? ` · ${t("footer.cvr", { cvr })}` : ""}
            {address ? ` · ${address}` : ""}
            {email ? (
              <>
                {" · "}
                <a href={`mailto:${email}`} className="hover:text-white">
                  {email}
                </a>
              </>
            ) : null}
          </p>
          <p>{t("footer.no_tracking")}</p>
        </div>
      </div>
    </footer>
  );
}

/** Sign-up consent: a clinic accepts the terms and the data processing agreement (GDPR art. 28). */
export function AcceptTerms({ error }: { error?: string }) {
  const { t } = useT();
  const link = "font-medium text-brand-700 hover:underline";
  return (
    <div className="space-y-1.5">
      <label className="flex items-start gap-3 text-sm text-slate-700">
        <input
          type="checkbox"
          name="acceptTerms"
          required
          aria-invalid={error ? true : undefined}
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 accent-brand-700"
        />
        <span>
          {t("auth.accept_terms_before")}{" "}
          <Link to="/terms" target="_blank" className={link}>
            {t("footer.terms")}
          </Link>{" "}
          {t("auth.accept_terms_and")}{" "}
          <Link to="/dpa" target="_blank" className={link}>
            {t("footer.dpa")}
          </Link>
          {t("auth.accept_terms_after")}
        </span>
      </label>
      {error ? <p className="text-sm text-red-700">{error}</p> : null}
    </div>
  );
}

/** Form values with the terms checkbox as the boolean the API expects. */
export const withAcceptTerms = (values: Record<string, string>) => ({
  ...values,
  acceptTerms: values.acceptTerms === "on",
});
